import { useCallback, useEffect, useState } from "react";

import { OspToast } from "@ospweb/sdk-ui";
import { Provider } from "@reown/appkit";
import log from "logbeacon";
import { useAppKitProvider } from "@reown/appkit/react";
import { useQueryClient } from "@tanstack/react-query";
import { useSignMessage } from "wagmi";
import { BindTypeEnum } from "../features/api/openApi";
import { QUERY_KEYS, config } from "../config";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import { useConnect } from "./useConnectWallet";
import { useSingleBind } from "./useSingleBind";
import Translations from "@translate/hooks/useBindWallet";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

export const useBindWallet = ({
  callback,
  claimCallback,
}: {
  callback?: (walletAddress: string) => Promise<void> | void;
  claimCallback?: (
    walletAddress: string,
    chainId: number,
  ) => Promise<void> | void;
}) => {
  const {
    address,
    onDisconnect,
    onConnect,
    isConnected,
    isConnecting,
    chainId,
    connectRef,
    caipAddress,
  } = useConnect();
  const { memberApi } = useOspApiClientProvider();
  const queryClient = useQueryClient();
  const { signMessageAsync } = useSignMessage();
  const { bind } = useSingleBind({ type: BindTypeEnum.WALLET });
  const [isLoading, setIsLoading] = useState(false);
  const { walletProvider } = useAppKitProvider<Provider>("solana");

  // function to sing a msg  solana wallet
  const handleSignMsg = useCallback(
    async (message: string) => {
      try {
        if (!walletProvider || !address) {
          throw Error("user is disconnected");
        }
        log.info("[Bind Wallet] signMessage solana params", message);
        const encodedMessage = new TextEncoder().encode(message);
        //@ts-expect-error :ignore the type error
        const sig = await walletProvider.signMessage(encodedMessage);
        log.info("[Bind Wallet] signMessage solana result", sig);
        const signatureHex = Buffer.from(sig).toString("hex");
        return signatureHex;
      } catch (error) {
        log.error("[Bind Wallet] signMessage solana error", error);
        throw error;
      }
    },
    [address, walletProvider],
  );

  const onReconnect = useCallback(async () => {
    log.debug(
      "[Bind Wallet] onReconnect",
      address,
      isConnected,
      connectRef.current,
    );
    if (isConnected) {
      // 如果钱包已连接，先断开连接
      await onDisconnect();
    }
    await onConnect();
    // connectRef 是 ref，不需要加入依赖数组
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDisconnect, onConnect, isConnected]);

  const handleFail = useCallback(
    (code?: string) => {
      onDisconnect();
      if (code === "106032") {
        // 超出绑定数量
        OspToast.error({
          content: I18n.t(
            "Binding failed, the number of bindable wallets has exceeded",
          ),
        });
      } else if (code === "106034") {
        // 重复绑定
        OspToast.error({
          content: I18n.t("This wallet has been bound"),
        });
      } else {
        OspToast.error({
          content: I18n.t("Binding failed，Please try again."),
        });
      }
    },
    [onDisconnect],
  );

  const onFinishEvent = useCallback(() => {
    setIsLoading(false);
    onDisconnect();
  }, [onDisconnect]);

  const onBind = useCallback(
    async (token: string, chainName: string = "BASE") => {
      if (address) {
        log.debug("[Bind Wallet] bind params", address, token);
        const res = await bind({
          address: address,
          token,
          chainName,
        });
        log.debug("[Bind Wallet] bind result", res);

        if (res?.error) {
          setIsLoading(false);
          onDisconnect();
          return handleFail(res?.error?.code?.toString());
        }
        if (res?.data?.occupied) {
          log.info("[Bind Wallet][trace][error] occupied", res?.data);
          setIsLoading(false);
          onDisconnect();
          OspToast.error({
            content: I18n.t(
              "Binding failed，This wallet is already linked to another account.",
            ),
          });
          return;
        }
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_BIND_LIST],
          });
        }, 1000);
        if (callback) {
          // 调用回调函数，等待其执行完成
          await callback(address || "");
          log.debug("[文案]callback", "success", callback);
          onFinishEvent();
        } else if (claimCallback) {
          claimCallback(address || "", Number(chainId) || 0);
        } else {
          log.debug("[文案]callback", false);
          onFinishEvent();
          OspToast.success({
            content: I18n.t("Wallet connected successfully!"),
          });
        }
      }
    },
    [
      address,
      bind,
      onDisconnect,
      callback,
      handleFail,
      chainId,
      queryClient,
      onFinishEvent,
      claimCallback,
    ],
  );

  const onSignMessage = useCallback(async () => {
    // 防止重复调用
    if (isLoading) {
      log.debug("[文案]已在进行签名流程，跳过重复调用");
      return;
    }

    setIsLoading(true);

    try {
      log.info(
        "[Bind Wallet] getChallenge params",
        address,
        "CONNECT",
        config.chainConfig.id,
        caipAddress,
      );
      const response = await memberApi?.auth.getChallenge({
        address: address || "",
        type: caipAddress?.chainNamespace === "solana" ? "SIWS" : "CONNECT",
      });
      const message = response?.data?.data;
      log.info("[Bind Wallet] getChallenge result", message);
      if (caipAddress?.chainNamespace === "solana") {
        const res = await handleSignMsg(message?.text || "");
        log.info("[Bind Wallet] signMessage result", res);
        onBind(res, "SOLANA");
      } else {
        const res = await signMessageAsync({
          message: message?.text || "",
        });
        log.info("[Bind Wallet] signMessage result", res);
        onBind(res);
      }
    } catch (error) {
      log.error("[Bind Wallet] onSignMessage error", error);
      OspToast.error({
        content: I18n.t("Wallet rejected.Please try again.."),
      });
      setIsLoading(false);
      onDisconnect();
    }
  }, [address, signMessageAsync, memberApi, onBind, onDisconnect, isLoading]);

  useEffect(() => {
    log.info(
      "[Bind Wallet] connect status",
      address,
      isConnected,
      connectRef.current,
      isLoading,
    );
    if (address && isConnected && connectRef.current) {
      connectRef.current = false;
      onSignMessage();
    }
    // connectRef 是 ref，不需要加入依赖数组
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, isConnected]);

  return {
    onBind,
    onReconnect,
    isLoading,
    isConnecting,
    onFinishEvent,
  };
};
